/**
 * ShareOverlay.tsx — the tool-upload wizard, loaded and gated CLIENT-SIDE so any page can open it in
 * place. This is the picker + overlay that used to live inside `Share.tsx` behind the `/share` route's
 * blocking loader; it now loads its own data on `open`, so a host (the Profile "Publish" button) can
 * pop it over the current page with no navigation and no loader stall.
 *
 * WHAT MOVED HERE FROM Share.tsx, UNCHANGED IN BEHAVIOUR:
 *   - `shareLoader`'s reads and error handling (consent 409, no-approved-environment 503, 404 → feature
 *     disabled), now run inside an `open`-keyed effect that sets state instead of a route return.
 *   - The whole `FlowPicker`: the flow listing (V1 table / V2 FlowPickerReskin / V3 in-overlay picker),
 *     the connector pre-detect (`inspect-connectors`) with its out-of-order guard, draft markers, and
 *     the `<UploadOverlay>` mount. None of it changed — only where it is hosted.
 *
 * WHAT THIS ADDS OVER Share.tsx:
 *   - `open`/`onClose`/`returnFocusRef` — the same modal contract the profile panels use, so a host
 *     toggles it like any other panel. `/share` renders it always-open.
 *   - An optional `me` prop: a host that already has the user record (Profile has it from meLoader)
 *     passes it in, and we skip the duplicate GET /api/me. Omitted → we fetch it ourselves.
 *   - `onUploaded` — an optional callback so a host can react to a successful upload.
 *
 * TRUST MODEL, unchanged from Share.tsx: the browser sends only the selected flow's workflowId, the
 * metadata, and the targetEnvironmentId that CAME FROM the backend's /api/flows/mine response. It never
 * sends a Dataverse URL or an owner id, never holds a token, and never sees a flow definition.
 *
 * CSS HOST: we render inside our OWN `.tb-tools` section and inject both TOOLS_STYLES and UPLOAD_STYLES,
 * exactly as `Share()` did. The default skin (V3) portals to document.body and needs none of this, but
 * V1 renders inline and requires a `.tb-tools` ancestor + both style strings, and V2 renders inline and
 * inherits element CSS. Providing our own `.tb-tools` context lets every skin render correctly no matter
 * which page mounts us (e.g. Profile's `.tb-profile` root, which is not `.tb-tools`). The bare-element
 * rules that then apply to V1 are the already-documented `.tb-tools` state (see the note in shared.tsx),
 * contained to this subtree — they do not reach the host page's own controls.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  apiGet,
  apiPost,
  type CloudFlowSummary,
  type FlowsMineResponse,
  type InspectConnectorsResult,
  type ToolRefData,
  type UploadResult,
} from './api';
import { TOOLS_STYLES } from './shared';
import type { MeResponse } from '../Profile';
import { UploadOverlay } from './upload/UploadOverlay';
import { draftsAvailable, loadDraft } from './upload/draftStore';
import { UPLOAD_STYLES } from './upload/styles';
import { useSkin } from './upload/skinStore';
import { FlowPickerReskin } from './upload/design/FlowPickerReskin';

/**
 * The overlay's OWN modal-shell styles — a fixed, centred backdrop + scrollable panel, so ShareOverlay
 * presents as a dialog over whatever page mounts it, NOT as a section appended at the bottom of that
 * page's flow. It is self-contained on purpose: the profile panels' equivalent (`.tb-modal__backdrop`)
 * lives in Profile.tsx's inline PROFILE_STYLES and is therefore absent on the standalone `/share` route,
 * so relying on it would make the shell appear on /me but collapse on /share. Carrying our own copy —
 * the same pattern we already use for TOOLS_STYLES / UPLOAD_STYLES — keeps the surface host-agnostic:
 * identical presentation from the Profile "Publish" button and from `/share`.
 *
 * Classnames are `so-` (share overlay) to avoid colliding with either host's own modal classes. The
 * backdrop sits at z-index 40; the V3 upload wizard portals to document.body at a higher layer (see
 * UPLOAD_STYLES), so once a flow is chosen the wizard covers this shell rather than nesting inside it.
 */
const SHARE_OVERLAY_SHELL_STYLES = `
  .so-backdrop {
    position: fixed; inset: 0; z-index: 40;
    background: rgba(0, 0, 0, 0.45);
    display: flex; align-items: flex-start; justify-content: center;
    padding: clamp(1rem, 5vh, 4rem) 1rem;
    overflow-y: auto;
  }
  .so-panel {
    background: var(--tb-bg); color: var(--tb-ink);
    border: 1px solid var(--tb-line); border-radius: 10px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
    padding: 1.25rem 1.5rem;
    width: 100%; max-width: 60rem;
    /* The panel scrolls internally if the flow list is long; the backdrop also scrolls as a fallback. */
    max-height: min(88vh, 52rem); overflow-y: auto;
  }
  /* The panel IS the .tb-tools CSS host, so cancel that class's own max-width (it would fight the
     panel's) — the .so-panel width rule above is the one we want here. */
  .so-panel.tb-tools { max-width: 60rem; }
  .so-panel > h2:first-child, .so-panel > h1:first-child { margin-top: 0; }
  /* Bare mode: while the V3 wizard is portaled over the shell (and during the auto-start pre-open
     window), the panel has no visible landing content, so strip its surface entirely. Otherwise the
     bright card paints behind the wizard and can flash through the wizard's scrim during the P1→P2
     frame remount. It stays in the DOM (it is still the .tb-tools CSS host + the aria dialog), just
     with nothing drawn. */
  .so-panel--bare {
    background: transparent; border-color: transparent; box-shadow: none;
    padding: 0; max-height: none; overflow: visible;
  }
  /* Visually-hidden but still announced: used for the dialog heading while the landing is hidden, so
     the aria-labelledby target and focus anchor stay present without any white text painting over the
     bare panel. Self-contained here (not relying on a global .sr-only) so it works under any host. */
  .so-visually-hidden {
    position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
    overflow: hidden; clip: rect(0, 0, 0, 0); clip-path: inset(50%); white-space: nowrap; border: 0;
  }
`;

export interface ShareOverlayProps {
  /** Mounts and begins loading when true; renders nothing when false. */
  open: boolean;
  /** Close the surface (overlay + any gating shell). The host restores focus via returnFocusRef. */
  onClose: () => void;
  /**
   * Optional host-provided user record, to avoid a duplicate GET /api/me. When omitted (the /share
   * wrapper), ShareOverlay fetches it itself. Drafts are keyed per user, so this must be the SAME
   * identity the loader would have fetched — Profile's meLoader and /api/me return the same record.
   */
  me?: MeResponse;
  /** Where to return focus after close; mirrors the AvatarPanel/BadgePanel/FollowingPanel contract. */
  returnFocusRef?: React.RefObject<HTMLButtonElement>;
  /** Optional: fired after a successful upload, so a host can revalidate or show its own banner. */
  onUploaded?: (result: UploadResult) => void;
  /**
   * Skip the picker's "Upload a tool" landing and go straight to choosing an automation, once the
   * flows have loaded. The Profile "Publish" button sets this: a Publish click should land directly on
   * the automation-pick step, not on an intermediate "Upload a tool" button. Under V3 (default) that
   * means opening the wizard at its first frame (P1, the flow picker); under V2 it opens the picker
   * modal; under V1 the flow TABLE is already the pick, so there is nothing to skip.
   */
  autoStart?: boolean;
}

/** The consent/environment states the flows route can report, carried through as data (never a crash). */
type FlowsState =
  | { kind: 'ok'; flows: FlowsMineResponse; refData: ToolRefData; me: MeResponse }
  | { kind: 'consentRequired'; message: string }
  | { kind: 'noApprovedEnvironment' }
  | { kind: 'error'; status: number; code: string | null };

/**
 * The client-side load phase. `loading` shows a spinner in the shell; the terminal states mirror what
 * `shareLoader` used to return (signedOut / featureDisabled / ready+FlowsState), so the gating UI is
 * the same, just driven by state instead of a route loader.
 */
type LoadPhase =
  | { kind: 'loading' }
  | { kind: 'signedOut' }
  | { kind: 'featureDisabled' }
  | { kind: 'ready'; flows: FlowsState };

/**
 * Fetch the user's cloud flows, the form reference data, and (unless the host supplied it) their own
 * user record — the exact reads and error mapping `shareLoader` did, run client-side. A 401 → signed
 * out; a 404 on either Dataverse route → feature disabled. A 409 (consent) or 503 (no approved
 * environment) from the flows route is carried through as data so the shell can explain it, not throw.
 *
 * `hostMe`, when present, short-circuits the /api/me read: the host already has the record and drafts
 * key off the same identity, so re-fetching it would be a wasted round-trip.
 */
async function loadFlows(hostMe: MeResponse | undefined): Promise<LoadPhase> {
  const flowsRes = await apiGet<FlowsMineResponse>('/api/flows/mine', {
    treat404AsFeatureDisabled: true,
  });
  if (flowsRes.kind === 'unauthorized') return { kind: 'signedOut' };
  if (flowsRes.kind === 'featureDisabled') return { kind: 'featureDisabled' };

  if (flowsRes.kind === 'error') {
    if (flowsRes.status === 409) {
      return {
        kind: 'ready',
        flows: {
          kind: 'consentRequired',
          message:
            'Sign out and sign back in so the Dataverse permission is granted, then return here.',
        },
      };
    }
    if (flowsRes.status === 503 && flowsRes.code === 'no_approved_environment') {
      return { kind: 'ready', flows: { kind: 'noApprovedEnvironment' } };
    }
    return {
      kind: 'ready',
      flows: { kind: 'error', status: flowsRes.status, code: flowsRes.code },
    };
  }

  // Flows loaded — now the form reference data, and the user record too unless the host gave us one.
  // A 404 on refdata is still "feature disabled"; /api/me is always mounted.
  const refRes = await apiGet<ToolRefData>('/api/tools/refdata', {
    treat404AsFeatureDisabled: true,
  });
  if (refRes.kind === 'unauthorized') return { kind: 'signedOut' };
  if (refRes.kind === 'featureDisabled') return { kind: 'featureDisabled' };
  if (refRes.kind === 'error') {
    return { kind: 'ready', flows: { kind: 'error', status: refRes.status, code: refRes.code } };
  }

  let me = hostMe;
  if (!me) {
    const meRes = await apiGet<MeResponse>('/api/me');
    if (meRes.kind === 'unauthorized') return { kind: 'signedOut' };
    if (meRes.kind === 'error') {
      return { kind: 'ready', flows: { kind: 'error', status: meRes.status, code: meRes.code } };
    }
    me = meRes.body;
  }

  return { kind: 'ready', flows: { kind: 'ok', flows: flowsRes.body, refData: refRes.body, me } };
}

export function ShareOverlay({
  open,
  onClose,
  me,
  returnFocusRef,
  onUploaded,
  autoStart,
}: ShareOverlayProps) {
  const [phase, setPhase] = useState<LoadPhase>({ kind: 'loading' });
  /** The panel heading, focused on open so the dialog announces itself (mirrors the profile panels). */
  const headingRef = useRef<HTMLHeadingElement>(null);
  /**
   * True while the ok-phase FlowPicker has hidden its landing chrome — i.e. the V3 wizard is portaled
   * over the shell, or we are in the auto-start pre-open window. In that state the panel draws nothing
   * (`.so-panel--bare`) so no bright card sits behind the wizard to flash through its scrim. FlowPicker
   * owns the condition (it knows the skin + open flow) and reports it up.
   */
  const [panelBare, setPanelBare] = useState(false);
  /** Bare mode only applies in the ok-phase (where FlowPicker owns it); gating states always paint. */
  const inOkPhase = phase.kind === 'ready' && phase.flows.kind === 'ok';
  /**
   * When opened via Publish (autoStart), the transient "loading your flows" step must NOT flash up a
   * visible "Share a tool" card with a Close button — that outdated intermediate screen is exactly what
   * we are deactivating. So while auto-starting and still loading, the shell holds INVISIBLY (bare panel,
   * heading + status hidden, no Close): the dark backdrop shows, then the wizard opens straight onto the
   * automation-pick step. The real gating states (signed-out, consent, no-environment, error) still paint
   * a card even under autoStart — those are messages the user genuinely needs, not a loading flash.
   */
  const loadingSilently = (autoStart ?? false) && phase.kind === 'loading';

  // Load on open false→true. The `ignore` flag drops a stale result if the overlay is closed or
  // unmounted before the fetch resolves, so a slow /api/flows/mine can never setState on a dead
  // surface. Re-loads on each reopen — cheap, and the perf goal is only that the PAGE is instant.
  useEffect(() => {
    if (!open) return;
    let ignore = false;
    setPhase({ kind: 'loading' });
    void loadFlows(me).then((next) => {
      if (!ignore) setPhase(next);
    });
    return () => {
      ignore = true;
    };
  }, [open, me]);

  // Move focus into the panel on open, and return it to the trigger on unmount — the same contract the
  // profile panels use (a host mounts this conditionally, so unmount == close). `phase` is a dep so the
  // heading is focused once the ok-phase FlowPicker heading actually exists, not just on first paint.
  // On /share the component is mounted for the route's lifetime, so the return fires only on route-away.
  useEffect(() => {
    if (!open) return;
    headingRef.current?.focus();
    const trigger = returnFocusRef?.current;
    return () => trigger?.focus();
  }, [open, returnFocusRef, phase.kind]);

  if (!open) return null;

  return (
    // A fixed, centred modal backdrop so the picker appears OVER the host page instead of appended to
    // the bottom of its flow. Backdrop click and Escape dismiss it (matching the profile panels); when
    // the V3 wizard is open it portals above this shell, so those handlers only ever close the picker.
    <div
      className="so-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        // Close on Escape — but only when it came from within this shell's own DOM. The V3 upload
        // wizard portals to document.body (outside this subtree) yet is a REACT child of FlowPicker, so
        // its Escape still bubbles here through the React tree; the wizard owns its own Escape (it steps
        // back to the picker), and we must not also tear down the whole overlay. `currentTarget.contains
        // (target)` is false for the portaled wizard, true for the picker/gating shell — exactly the
        // split we want.
        if (e.key === 'Escape' && e.currentTarget.contains(e.target as Node)) {
          onClose();
        }
      }}
    >
      <style>{SHARE_OVERLAY_SHELL_STYLES}</style>
      {/* The panel is BOTH the dialog and the `.tb-tools` CSS host (see this file's header): V1 renders
          inline and needs a `.tb-tools` ancestor + both style strings; V3 portals out but is harmless
          here. TOOLS_STYLES/UPLOAD_STYLES are injected unconditionally, exactly as `Share()` did. */}
      <section
        className={`so-panel tb-tools${(panelBare && inOkPhase) || loadingSilently ? ' so-panel--bare' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-overlay-heading"
      >
        <style>{TOOLS_STYLES}</style>
        <style>{UPLOAD_STYLES}</style>

        {phase.kind === 'ready' && phase.flows.kind === 'ok' ? (
          <FlowPicker
            flows={phase.flows.flows}
            refData={phase.flows.refData}
            me={phase.flows.me}
            headingRef={headingRef}
            autoStart={autoStart ?? false}
            onLandingHiddenChange={setPanelBare}
            onClose={onClose}
            onUploaded={onUploaded}
          />
        ) : (
          <>
            {/* Under autoStart the loading step is silent (see loadingSilently): the heading goes
                visually-hidden so no "Share a tool" card flashes before the wizard opens. */}
            <h2
              id="share-overlay-heading"
              ref={headingRef}
              tabIndex={-1}
              className={loadingSilently ? 'so-visually-hidden' : undefined}
            >
              Share a tool
            </h2>

            {phase.kind === 'loading' && (
              <p className={loadingSilently ? 'so-visually-hidden' : 'tb-muted'} role="status">
                Loading your flows…
              </p>
            )}
            {phase.kind === 'signedOut' && (
              <p style={{ maxWidth: '52ch' }}>
                Sign in with your work account to continue.{' '}
                <a
                  className="tb-signin-btn"
                  href={`/auth/login?returnTo=${encodeURIComponent('/share')}`}
                >
                  Sign in with your work account
                </a>
              </p>
            )}
            {phase.kind === 'featureDisabled' && (
              <p style={{ maxWidth: '52ch' }}>
                Tool sharing and adoption are not available in this build.
              </p>
            )}
            {phase.kind === 'ready' && phase.flows.kind === 'consentRequired' && (
              <div className="tb-note tb-note--warn" role="alert">
                {phase.flows.message}
              </div>
            )}
            {phase.kind === 'ready' && phase.flows.kind === 'noApprovedEnvironment' && (
              <div className="tb-note tb-note--warn" role="alert">
                No approved Power Automate environment is configured, so there is nothing to share
                from yet.
              </div>
            )}
            {phase.kind === 'ready' && phase.flows.kind === 'error' && (
              <div className="tb-note tb-note--warn" role="alert">
                Could not load your cloud flows ({phase.flows.status}
                {phase.flows.code ? `: ${phase.flows.code}` : ''}).
              </div>
            )}

            {/* Every non-ok phase needs its own dismiss, since the FlowPicker — which owns the ok-phase
                Close — is not mounted. On /share this steps back in history; on a host page it clears
                the host's open state (which returns focus to the trigger). Hidden while loading silently
                (autoStart): there is no card to dismiss, and the wizard is about to take over. */}
            {!loadingSilently && (
              <p>
                <button type="button" className="tb-signin-btn" onClick={onClose}>
                  Close
                </button>
              </p>
            )}
          </>
        )}
      </section>
    </div>
  );
}

/** The flow picker. Rendered only when flows, refData and the user record all loaded successfully. */
function FlowPicker({
  flows,
  refData,
  me,
  headingRef,
  autoStart,
  onLandingHiddenChange,
  onClose,
  onUploaded,
}: {
  flows: FlowsMineResponse;
  refData: ToolRefData;
  me: MeResponse;
  // The shell's dialog heading, focused on open — this component owns the ok-phase heading, so the
  // ref lives here and the shell focuses whatever heading is currently mounted.
  headingRef: React.RefObject<HTMLHeadingElement>;
  // Skip straight to the automation-pick step on mount (see ShareOverlayProps.autoStart).
  autoStart: boolean;
  // Report up whenever the picker landing is hidden (V3 wizard open / auto-start pre-open window), so
  // the shell can strip its panel surface and nothing bright flashes behind the portaled wizard.
  onLandingHiddenChange: (hidden: boolean) => void;
  onClose: () => void;
  // Passed straight through from ShareOverlayProps, so its type includes `undefined` to satisfy
  // exactOptionalPropertyTypes when the host omits it.
  onUploaded: ((result: UploadResult) => void) | undefined;
}) {
  /** The flow whose overlay is open, or null when the picker is showing. */
  const [openFlow, setOpenFlow] = useState<CloudFlowSummary | null>(null);
  /** Bumped after an upload or a draft change so the "Draft saved" markers re-read localStorage. */
  const [draftEpoch, setDraftEpoch] = useState(0);
  const [uploaded, setUploaded] = useState<UploadResult | null>(null);
  /** Which entry affordance to show: the original table (classic) or our P1-style picker (reskin). */
  const skin = useSkin();
  /** Reskin only: whether the P1-style picker modal (search + flow table) is open. */
  const [pickerOpen, setPickerOpen] = useState(false);

  // ── Connector pre-detect (existing Stage 3.5b logic, unchanged in behaviour) ────────────────────
  //
  // Decides only the TONE of the overlay's Requirements field. 'has' → mandatory; anything else →
  // optional, with the server's 422 as the real gate. Fails OPEN so a pre-detect hiccup can never
  // hide a field the server will require.
  const [connectorState, setConnectorState] = useState<
    'unknown' | 'checking' | 'has' | 'none' | 'error'
  >('unknown');
  /** The workflow id whose inspect result we most recently requested — drops out-of-order replies. */
  const inspectForRef = useRef<string>('');

  async function inspectConnectors(id: string) {
    if (!id) {
      setConnectorState('unknown');
      return;
    }
    inspectForRef.current = id;
    setConnectorState('checking');
    const res = await apiPost<InspectConnectorsResult>('/api/tools/inspect-connectors', {
      targetEnvironmentId: flows.environment_id,
      workflowId: id,
    });
    if (inspectForRef.current !== id) return; // superseded by a newer selection
    setConnectorState(res.kind === 'ok' ? (res.body.has_connectors ? 'has' : 'none') : 'error');
  }

  /** Which of the listed flows already have a saved draft, so the picker can say so. */
  const draftedIds = useMemo(() => {
    void draftEpoch; // re-read whenever a draft may have changed
    const ids = new Set<string>();
    if (!draftsAvailable()) return ids;
    for (const f of flows.flows) {
      if (loadDraft(me.user_id, f.workflowId)) ids.add(f.workflowId);
    }
    return ids;
  }, [flows.flows, me.user_id, draftEpoch]);

  /** Open the overlay for one flow, kicking off its connector pre-detect at the same time. */
  function openFor(flow: CloudFlowSummary) {
    setUploaded(null);
    setPickerOpen(false);
    setOpenFlow(flow);
    void inspectConnectors(flow.workflowId);
  }

  /**
   * V3 ONLY: open the overlay with NO automation chosen, because under V3 the picker (P1) is the
   * overlay's own first frame — that is the whole point of V3, and the design draws it that way.
   *
   * `UploadOverlayProps.flow` is required and the overlay only mounts when it is set, so a flow has
   * to be passed. The FIRST flow is passed as a placeholder and the overlay ignores it: V3 starts
   * its own `chosenFlow` at null and shows P1 regardless (see UploadOverlayPages). Nothing is
   * uploaded for the placeholder — the hook that reads `flow` is not constructed until P1's pick.
   *
   * THE CONNECTOR PRE-DETECT still has to run for the REAL pick, and that pick now happens inside
   * the overlay, so V3 reports it back through `onFlowChosen`. Same call, same behaviour, just
   * triggered from one frame later. It decides only the TONE of the Requirements field, and it fails
   * open, so a late or missed pre-detect cannot hide a field the server will require.
   */
  function openWizardV3() {
    const first = flows.flows[0];
    if (!first) return;
    setUploaded(null);
    setOpenFlow(first);
    setConnectorState('unknown');
  }

  // The outer "Share a tool" landing (heading + intro + "Upload a tool" button + Close) is DEAD under
  // V3. V3's picker is the wizard's own first frame (P1), so an outer button that just opens the wizard
  // is pure duplication — an outdated intermediate screen. So under V3 we open the wizard IMMEDIATELY on
  // mount and never draw that landing: Publish (and /share) land straight on the automation-pick step,
  // and there is nothing behind the wizard to flash up as a "loading screen".
  //
  // Fires once (the ref guard) with zero flows excepted — with no flows there is nothing to open, so we
  // fall through to the "no cloud flows" message instead of an empty wizard. V2 still uses `autoStart`
  // to decide whether to open its picker MODAL immediately (Publish) or wait behind its button (/share);
  // V1's table is itself the pick step, so it has nothing to auto-open.
  const didAutoOpen = useRef(false);
  useEffect(() => {
    if (didAutoOpen.current) return;
    if (flows.flows.length === 0) return; // nothing to open — the "no cloud flows" message shows
    if (skin === 'v3') {
      didAutoOpen.current = true;
      openWizardV3();
    } else if (skin === 'v2' && autoStart) {
      didAutoOpen.current = true;
      setPickerOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; openWizardV3 is stable
  }, [autoStart, skin, flows.flows.length]);

  // Whether to close the WHOLE overlay when the wizard exits, rather than dropping back to the outer
  // landing. Under V3 the landing is dead (above), so Escape / Cancel / back-out of the wizard must
  // dismiss the whole surface — there is no "Share a tool" screen to return to. V1/V2 pick BEFORE the
  // overlay, so their wizard-close legitimately returns to that pick step.
  const wizardCloseEndsOverlay = skin === 'v3';

  // Under V3 the landing never renders and the wizard portals over the whole shell, so the panel behind
  // it is dead weight that could otherwise flash through the wizard's scrim. `landingHidden` is true for
  // the entire V3 lifetime (from first paint, since V3 auto-opens): the landing chrome stays out of the
  // DOM and the shell panel is stripped to nothing (`.so-panel--bare`). V1/V2 keep their landing.
  const landingHidden = skin === 'v3' && flows.flows.length > 0;

  // Keep the shell's panel-bare state in step with whether the landing is showing. Reported through a
  // callback rather than lifted state so FlowPicker stays the single owner of the skin/open-flow logic.
  useEffect(() => {
    onLandingHiddenChange(landingHidden);
  }, [landingHidden, onLandingHiddenChange]);

  return (
    <>
      {/* The dialog heading stays mounted even when the landing is hidden — it is the aria-labelledby
          target and the focus anchor — but goes visually-hidden so no white text floats over the bare
          panel while the wizard is portaled on top. `.so-visually-hidden` is in SHARE_OVERLAY_SHELL_STYLES. */}
      <h2
        id="share-overlay-heading"
        ref={headingRef}
        tabIndex={-1}
        className={landingHidden ? 'so-visually-hidden' : undefined}
      >
        Share a tool
      </h2>

      {!landingHidden && (
        <>
          {uploaded && (
            <div className="tb-note tb-note--ok" role="status">
              <strong>{uploaded.name}</strong> was shared as a Draft
              {typeof uploaded.documented_component_count === 'number' &&
              uploaded.documented_component_count > 0
                ? `, with ${uploaded.documented_component_count} ${uploaded.documented_component_count === 1 ? 'step' : 'steps'} documented`
                : ''}
              . <a href={`/tools/${uploaded.tool_id}`}>Open the tool detail page</a>
            </div>
          )}

          <h3>Your Power Automate cloud flows</h3>
          {flows.flows.length === 0 ? (
            <p className="tb-muted">
              You have no cloud flows in {flows.environment_name}. Create one in Power Automate,
              then come back here.
            </p>
          ) : skin === 'v2' ? (
            /* Reskin entry (→ our P1). A single call-to-action button; the flow list becomes the first
           modal step (search + table), matching our design. Behaviour is unchanged — picking a row
           and pressing Next calls the SAME `openFor` the classic table does. The picker mounts the
           reskin styles so its chrome matches the overlay it hands off to. */
            <>
              <p>
                Choose the automation you want to share. You will be asked to explain each of its
                steps, so a colleague can set it up for themselves.
              </p>
              <button type="button" onClick={() => setPickerOpen(true)}>
                Upload a tool
              </button>

              {pickerOpen && (
                <FlowPickerReskin
                  flows={flows.flows}
                  environmentId={flows.environment_id}
                  draftedIds={draftedIds}
                  onPick={openFor}
                  onClose={() => setPickerOpen(false)}
                />
              )}
            </>
          ) : (
            <>
              <p>
                Choose the automation you want to share. You will be asked to explain each of its
                steps, so a colleague can set it up for themselves.
              </p>
              <table>
                <caption className="tb-muted" style={{ captionSide: 'bottom', textAlign: 'left' }}>
                  {flows.flows.length} {flows.flows.length === 1 ? 'flow' : 'flows'} in{' '}
                  {flows.environment_name}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Flow name</th>
                    <th scope="col">Created by you</th>
                    <th scope="col">Owned by you</th>
                    <th scope="col">Draft</th>
                    <th scope="col"> </th>
                  </tr>
                </thead>
                <tbody>
                  {flows.flows.map((f) => (
                    <tr key={f.workflowId}>
                      <td>{f.name}</td>
                      <td>{f.createdByMatch ? 'Yes' : 'No'}</td>
                      <td>{f.ownerMatch ? 'Yes' : 'No'}</td>
                      <td>
                        {draftedIds.has(f.workflowId) ? (
                          <strong>Saved</strong>
                        ) : (
                          <span className="tb-muted">—</span>
                        )}
                      </td>
                      <td>
                        <button type="button" onClick={() => openFor(f)}>
                          {draftedIds.has(f.workflowId) ? 'Continue' : 'Share this flow'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}

      {openFlow && (
        <UploadOverlay
          flow={openFlow}
          // V3 ONLY (ignored by V1/V2): the rows P1 lists, since V3's picker is inside the overlay.
          flows={flows.flows}
          // V3 ONLY: P1's pick, reported back so the connector pre-detect runs for the REAL
          // automation (see openWizardV3). V1/V2 pick before the overlay, so they never call it.
          onFlowChosen={(flow) => {
            setOpenFlow(flow);
            void inspectConnectors(flow.workflowId);
          }}
          environmentId={flows.environment_id}
          environmentName={flows.environment_name}
          refData={refData}
          userId={me.user_id}
          builderName={me.display_name}
          builderDepartment={me.profile?.department ?? null}
          builderJobTitle={me.profile?.job_title ?? null}
          // UP-F02: contact address, pre-filled read-only from the user record. GET /api/me returns
          // it as MeResponse.email (nullable — a user record may carry no email). Ported here from the
          // old inline Share.tsx in the staging merge: that file became a thin route wrapper, and this
          // is now the ONLY place the wizard is mounted, so dropping the prop would have silently
          // emptied the "Contact:" row UploadOverlayReskin reads off vm.props.builderEmail.
          builderEmail={me.email}
          connectorState={connectorState}
          onClose={() => {
            // Where the wizard's dismiss leads depends on the skin. Under V3 the outer "Share a tool"
            // landing is dead (V3's own P1 IS the pick step), so dismissing the wizard closes the WHOLE
            // overlay — there is no intermediate screen to return to. Under V1/V2 the pick happened on
            // the outer landing before the overlay, so dismiss drops back to it (KEEPING the draft —
            // see draftStore.ts), re-reading the markers so a newly saved draft shows straight away.
            if (wizardCloseEndsOverlay) {
              onClose();
              return;
            }
            setOpenFlow(null);
            setDraftEpoch((n) => n + 1);
          }}
          onUploaded={(result) => {
            // Tell the host first (Profile revalidates its uploads list, /share is a no-op here).
            onUploaded?.(result);
            // Under V3 the wizard has ALREADY shown its own success/confirmation page (deferCompletion),
            // and the user pressed Finish — so there is nothing to return to. Close the whole overlay
            // rather than dropping to a bare panel with a redundant banner. V1/V2 have no such success
            // page, so they fall back to the outer landing with the "shared as a Draft" banner.
            if (wizardCloseEndsOverlay) {
              onClose();
              return;
            }
            setUploaded(result);
            setOpenFlow(null);
            setDraftEpoch((n) => n + 1);
          }}
        />
      )}

      {/* Close the whole surface. On /share this steps back in history; on a host page it clears the
          host's open state (which returns focus to the trigger). Hidden while the landing is — the V3
          wizard portaled on top owns its own dismiss (and stepping back to the picker), so a second
          Close floating under a transparent panel would be both invisible and confusing. */}
      {!landingHidden && (
        <p>
          <button type="button" className="tb-signin-btn" onClick={onClose}>
            Close
          </button>
        </p>
      )}
    </>
  );
}
